import React, { useEffect, useState, useContext, useCallback, useRef, useMemo } from 'react';
import Select from 'react-select';
import { useChartData } from './RhythmUtils';
import { CChartLine } from '@coreui/react-chartjs';
import { Oval } from 'react-loader-spinner';
import {
  CCard,
  CCardBody,
  CCardFooter,
  CCardTitle,
  CCol,
  CRow,
} from '@coreui/react';
import { getAuth } from 'firebase/auth';
import { getStyle, hexToRgba } from '@coreui/utils';
import WidgetManager from '../../widgets/WidgetManager';
import {
  getTotalDaysInMonth,
  getFirstAndLastDayOfMonth,
  mainChartOpts,
} from './RhythmUtils';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import './RhythmCard.css';
import { AuthUserContext } from '../../session/context';
import yourIcon from '../../../assets/images/mie_sprite.png';
import Logger from '../../../utils/Logger';
import { fetchGoogleData, fetchGoogleCalendarData } from './GoogleHelper';
import { fetchAppleData } from './AppleHelper';
import { fetchDebitsFromFHIR, fetchObservationsFromFHIR, fetchWalletData } from './FhirHelper';
import { getFallbackData } from './RhythmHelpers';
import { isVAT } from 'validator';
import CircadianAIAssistant from '../../../components/CircadianAIAssistant';

const useLocalStorage = (key, initialValue) => {
  // State to store our value
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.log(error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function
  const setValue = useCallback((value) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;

      // Only update state if the value has changed
      if (JSON.stringify(valueToStore) !== JSON.stringify(storedValue)) {
        setStoredValue(valueToStore);
        localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (error) {
      console.log(error);
    }
  }, [key, storedValue]); // Add dependencies for useCallback

  return [storedValue, setValue];
};


const RhythmCard = () => {
  const context = useContext(AuthUserContext);
  // Logger.debug('AuthUserContext values:', {
  //   ...context,
  //   hasIsTokenExpired: !!context.isTokenExpired,
  //   hasGetValidToken: !!context.getValidToken,
  //   functions: Object.keys(context).filter(key => typeof context[key] === 'function')
  // });
  const {
    authUser,
    isTokenValid,
    getValidToken,
    isTokenExpired,
    refreshToken
  } = useContext(AuthUserContext);

  const [date, setDate] = useState(new Date().toISOString());
  const { cr_data, setCRData } = useChartData();
  const [steps, setStepsData] = useState(0);
  const [calories, setCaloriesData] = useState(0);
  const [caloriesToday, setCaloriesTodayData] = useState(0);
  const [sleep, setSleepData] = useState(0);
  const [weight, setWeightData] = useState(0);
  const [nutrition, setNutritionData] = useState(0);
  const googleAccessToken = localStorage.getItem('googleAccessToken');
  const [googleCalendarData, setGoogleCalendarData] = useState([]); // For Google Calendar events
  const [appleCalendarData, setAppleCalendarData] = useState([]); // For Google Calendar events
  const appleAccessToken = localStorage.getItem('appleAccessToken');
  const [loading, setLoading] = useState(false);
  const [mood, setMood] = useState('');
  const [moodColor, setMoodColor] = useState('blue');
  const [isRefreshing, setIsRefreshing] = useState(false);
  let chartReference = {};
  const [timeAtDesk, setTAD] = useState('56');
  const [stationTemp, setStationTemp] = useState('27');
  const [humidity, setHumidity] = useState('50');
  const userSession = JSON.parse(localStorage.getItem('userSession')); // Parse userSession object
  const walletAddress = userSession?.walletAddress || null; // Retrieve walletAddress or set to null if not found
  const [selectedObservations, setSelectedObservations] = useLocalStorage('selectedObservations', []);
  const isMountedRef = useRef(true);
  const [persistedObservations, setPersistedObservations] = useLocalStorage('persistedObservations', {
    observationlist: [],
    observations: [],
  });
  const [state, setState] = useState({
    observationlist: persistedObservations.observationlist || [],
    observations: persistedObservations.observations || [],
    loading: true
  });
  const data = {
    steps,
    caloriesToday,
    stationTemp,
    humidity,
    weight,
    mood,
    timeAtDesk,
  };
  // Define the default widgets and maximum widgets
  const defaultWidgets = ['InsightsWidget', 'GptMiEWidget','HomeAssistantWidget','MoodWidget'];
  const maxWidgets = 5;
  const [statusMessage, setStatusMessage] = useState('');
  const processFHIRObservations = useCallback((fhirBundle) => {
    // If it's already in the correct format, return it
    if (Array.isArray(fhirBundle) && fhirBundle.length > 0 && fhirBundle[0].type) {
      //Logger.debug('Data already in correct format:', fhirBundle);
      return fhirBundle;
    }

    // Check if this is FHIR Bundle data
    if (fhirBundle.resourceType === 'Bundle' && fhirBundle.entry) {
     // Logger.debug('Processing FHIR Bundle format data');
      
      const { firstDay } = getFirstAndLastDayOfMonth();
      const totalDays = getTotalDaysInMonth(firstDay.getFullYear(), firstDay.getMonth());
      const groupedObservations = {};

      fhirBundle.entry.forEach(entry => {
        const observation = entry.resource;
        const type = observation.code.text;
        const date = new Date(observation.effectiveDateTime);
        const day = date.getUTCDate();
        const value = observation.valueQuantity.value;

        if (!groupedObservations[type]) {
          groupedObservations[type] = {
            type,
            data: Array(totalDays).fill(null),
            unit: observation.valueQuantity.unit,
            source: 'fhir'
          };
        }
        
        groupedObservations[type].data[day - 1] = value;
      });

      return Object.values(groupedObservations);
    }

    // Handle fallback data format
    if (fhirBundle.steps || fhirBundle.calories) {
     // Logger.debug('Processing fallback data');
      const observations = [];
      
      Object.entries(fhirBundle).forEach(([type, values]) => {
        observations.push({
          type: type.charAt(0).toUpperCase() + type.slice(1),
          data: values,
          unit: getUnitForMetric(type),
          source: 'fallback'
        });
      });

      return observations;
    }

    //Logger.debug('No valid data format detected');
    return [];
  }, []);

  const handleObservationChange = useCallback((selectedOptions) => {
    if (!selectedOptions) {
      setSelectedObservations([]);
      return;
    }

    setSelectedObservations(selectedOptions);

    const { firstDay } = getFirstAndLastDayOfMonth();
    const totalDays = getTotalDaysInMonth(firstDay.getFullYear(), firstDay.getMonth());
    const labels = Array.from({ length: totalDays }, (_, i) => (i + 1).toString());

    const datasets = state.observations
      .filter(observation => 
        selectedOptions.some(option => observation.type === option.label)
      )
      .map(observation => {
        const hue = Math.abs(observation.type.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)) % 360;
        return {
          label: observation.type,
          data: observation.data,
          backgroundColor: `hsla(${hue}, 70%, 50%, 0.2)`,
          borderColor: `hsl(${hue}, 70%, 50%)`,
          spanGaps: true,
          tension: 0.4
        };
      });

    setCRData({
      labels,
      datasets
    });
  }, [state.observations]);

  const fetchAllData = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      setIsRefreshing(true);

      const { firstDay, lastDay } = getFirstAndLastDayOfMonth();
     // Logger.debug('Fetching data for range:', firstDay, lastDay);

      let fhirData = await fetchObservationsFromFHIR(firstDay, lastDay, authUser);
    //  Logger.debug('Raw FHIR data:', fhirData);
      
      let processedObservations;
      
      // Check if we have valid FHIR data array
      if (Array.isArray(fhirData) && fhirData.length > 0) {
    //    Logger.debug('Using FHIR data array with entries:', fhirData.length);
        processedObservations = fhirData; // Already in correct format
        setStatusMessage(false);
      } else if (fhirData?.resourceType === 'Bundle' && fhirData?.entry?.length > 0) {
     ///   Logger.debug('Using FHIR Bundle data with entries:', fhirData.entry.length);
        processedObservations = processFHIRObservations(fhirData);
      } else {
     //   Logger.debug('FHIR data not available or empty, using fallback');
        setStatusMessage('FHIR data not available or empty, using fallback');
        const fallbackData = await getFallbackData(firstDay, lastDay, true);
     //   Logger.debug('Fallback data received:', fallbackData);
        processedObservations = processFHIRObservations(fallbackData);
      }

     // Logger.debug('Final processed observations:', processedObservations);

      // Create options for the select component
      const observationOptions = processedObservations
        .filter(obs => obs.data && obs.data.some(value => value !== null)) // Add null check for obs.data
        .map(obs => ({
          value: obs.type.toLowerCase(),
          label: obs.type,
          source: obs.source
        }));

    //  Logger.debug('Final observation options:', observationOptions);

      // Update state in a single operation
      setState(prev => ({
        ...prev,
        observations: processedObservations,
        observationlist: observationOptions,
        loading: false
      }));

      // Only set initial selections if none exist and we have options
      // if (selectedObservations.length === 0 && observationOptions.length > 0) {
      //   handleObservationChange(observationOptions);
      // }
      handleObservationChange(observationOptions);

    } catch (error) {
      Logger.error('Error fetching data:', error);
      toast.error('Failed to refresh data');
      setState(prev => ({ ...prev, loading: false }));
    } finally {
      setIsRefreshing(false);
    }
  }, [authUser, processFHIRObservations, handleObservationChange, selectedObservations.length]);

  // Initial data fetch - only run once
  useEffect(() => {
    fetchAllData();
  }, []); // Empty dependency array

  // Set up tick interval
  useEffect(() => {
    const timerID = setInterval(() => {
      setDate(new Date().toISOString());
      fetchAllData();
    }, 60000);

    return () => clearInterval(timerID);
  }, [fetchAllData]);

  // useEffect(() => {
  //   const fetchData = async () => {
  //     //if (!isTokenValid) return;

  //     try {
  //       const token = await getValidToken();
  //       if (!token) throw new Error('Could not get valid token');

  //       fetchAllData();
  //     } catch (error) {
  //       console.error('Error fetching data:', error);
  //     }
  //   };

  //   fetchData();
  // }, [isTokenValid, getValidToken]);

  useEffect(() => {
    if (selectedObservations.length > 0 && state.observations.length > 0) {
      handleObservationChange(selectedObservations);
    }
  }, [state.observations, selectedObservations, handleObservationChange]);

  // Move RefreshOverlay component outside of the return statement
  const RefreshOverlay = () => (
    <div className="overlay" style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <div className="overlay-content">
        <img src={yourIcon} alt="Loading Icon" className="loading-icon" />
        <div className="loading-text">MiEROS Loading...</div>
      </div>
    </div>
  );

  const ObservationSelect = useMemo(() => {
    // Logger.debug('Rendering Select with options:', {
    //   options: state.observationlist,
    //   selected: selectedObservations,
    //   loading: state.loading
    // });

    return (
      <Select
        isMulti
        value={selectedObservations}
        options={state.observationlist}
        onChange={handleObservationChange}
        closeMenuOnSelect={false}
        maxMenuHeight={200}
        placeholder={state.loading ? "Loading observations..." : "Select observations"}
        isLoading={state.loading}
        isDisabled={state.loading}
      />
    );
  }, [state.observationlist, state.loading, selectedObservations, handleObservationChange]);

  // Helper function to get units
  const getUnitForMetric = useCallback((metricName) => {
    const unitMap = {
      steps: 'count',
      calories: 'kcal',
      weight: 'kg',
      sleep: 'hours',
      activity: 'minutes',
      nutrition: 'calories',
      'Beers Drank': 'count',
      'Sleep Hours': 'hours'
    };
    return unitMap[metricName.toLowerCase()] || 'count';
  }, []);

  // Add chart reference
  const chartRef = useRef(null);

  // Add chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true
      }
    },
    plugins: {
      tooltip: {
        // ... existing tooltip options ...
        position: 'nearest',
        caretPadding: 10,
        xAlign: 'left',
        yAlign: 'left',
        // Optional: Add custom positioning
        callbacks: {
          // ... existing callbacks ...
        },
        // Optional: Add pixel offset
        caretX: -60, // Moves tooltip left/right
        caretY: 0,   // Moves tooltip up/down
      }
    },
  };

  // Add useEffect to log chart data changes
  // useEffect(() => {
  //       Logger.debug('Chart data updated:', {
  //             hasData: !!cr_data,
  //             datasets: cr_data?.datasets,
  //             labels: cr_data?.labels
  //       });
  // }, [cr_data]);

  // Add verification logging
  // useEffect(() => {
  //   Logger.debug('RhythmCard Context Values:', {
  //     hasAuthUser: !!authUser,
  //     hasIsTokenValid: typeof isTokenValid === 'boolean',
  //     getValidTokenType: typeof getValidToken,
  //     isTokenExpiredType: typeof isTokenExpired,
  //     refreshTokenType: typeof refreshToken
  //   });
  // }, [authUser, isTokenValid, getValidToken, isTokenExpired, refreshToken]);

  return (
    <>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <Oval height={80} width={80} color="#4fa94d" visible={true} ariaLabel="oval-loading" />
        </div>
      ) : (
        <div>
          <CRow>
            <CCol>
              <CCard style={{ position: 'relative' }}>
                {isRefreshing && <RefreshOverlay />}
                <CCardBody>
                  {statusMessage && (
                    <div className="alert alert-info" role="alert">
                      {statusMessage}
                    </div>
                  )}
                  <CRow>
                    <CCol sm="5">
                      <CCardTitle className="mb-0">Circadian Rhythm</CCardTitle>
                      <div style={{ marginTop: '20px', color: moodColor }} className="small mb-0">Last refreshed: {date}</div>
                    </CCol>
                    <CCol sm="5">
                      <CCardTitle className="mb-0">Datasets</CCardTitle>
                      <div className="rhythm-card-datasets-select">
                        {ObservationSelect}
                      </div>
                    </CCol>
                  </CRow>
                  <CRow>
                    <CCol>
                      <div style={{ height: '300px', marginTop: '20px' }}>
                        {cr_data && cr_data.datasets && (
                          <>

                            <CChartLine
                              ref={chartRef}
                              data={cr_data}
                              options={chartOptions}
                              style={{ height: '100%' }}
                            />
                          </>
                        )}
                      </div>
                    </CCol>
                  </CRow>
                </CCardBody>
                <CCardFooter>
                  {/* Circadian AI Assistant - Above WidgetManager */}
                  <CircadianAIAssistant 
                    authUser={authUser} 
                    onRefresh={fetchAllData}
                  />
                  
                  {/* Pass the defaultWidgets and maxWidgets as props to WidgetManager */}
                  <WidgetManager
                    data={data}
                    defaultWidgets={defaultWidgets}
                    maxWidgets={maxWidgets}
                  />
                  <div style={{ marginTop: '20px', color: moodColor }}>
                    Current Mood Color: {moodColor}
                  </div>
                </CCardFooter>
              </CCard>
            </CCol>
          </CRow>
        </div>
      )}
    </>
  );
};

export default RhythmCard;


